// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 809c53fd-5d48-4829-a14b-2ecc403ddb2f

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// A self-contained browser client for Cognito hosted-UI federation (PKCE).
//
// PORTABLE ON PURPOSE. This file is the sign-in tech Primordia and Scout
// share, extracted so "copy it to the other app" is copying ONE file:
//
//   * zero imports, zero framework — plain browser JS (fetch, crypto.subtle,
//     sessionStorage, URLSearchParams and nothing else);
//   * nothing app-specific — configuration is INJECTED via createSsoClient(),
//     so it knows no config module, no storage layout, no UI;
//   * consumable both ways: an ES import in a bundled app
//     (`import { createSsoClient } from './ssoClient'`) and a script tag in a
//     static page (`<script type="module" src="/ssoClient.js">` — it also
//     attaches window.createSsoClient for inline classic scripts to call).
//
// WHAT IT KNOWS THAT COST REAL TIME TO LEARN — do not re-learn these:
//
//   * The CSRF `state` must ride TO the authorize endpoint or the guard is
//     theatre. Primordia's original client generated and stored it but never
//     sent it, so every callback returned stateless and 100% of federated
//     sign-ins were refused, in every browser — undetectable until the first
//     provider was genuinely attached. Scout's original client has the
//     mirror-image flaw: it never used state at all, so its callback carries
//     no CSRF binding beyond what PKCE implies. This client generates it,
//     sends it, and requires it back.
//   * A callback with NO sign-in pending is STALE, not hostile — the address
//     bar autocompleting to an old `?code=...` URL is the classic source
//     (Edge does it persistently). It is dismissed softly ('stale'); the hard
//     'error' is reserved for a pending sign-in whose state does not match.
//   * The one-time code and state are stripped from the URL and the verifier
//     cleared on EVERY exit path, so a refresh can never replay them.
//   * Cognito reports an unattached/misspelled provider as a generic
//     invalid_request family on the redirect back; that means "not wired up
//     yet" and is reported as 'not-configured', not as a failure.
//
// The consumer decides what a session IS. complete() hands back raw tokens
// ({ id_token, access_token, refresh_token }); persisting them, decoding
// claims and deciding entitlement is the app's business, not this file's.
// ---------------------------------------------------------------------------

const DEFAULT_NOT_CONFIGURED_MSG = 'Single sign-on is not yet configured for this environment. '
  + 'Sign in with a Primordia account below, or ask an administrator to finish the directory setup.';

const NOT_CONFIGURED_CODES = ['invalid_request', 'unauthorized_client', 'unsupported_response_type', 'invalid_client'];

function b64url(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Cryptographically random, never Math.random: the verifier is the only thing
// standing between an intercepted code and a token.
function randomToken(bytes) {
  const buf = new Uint8Array(bytes || 32);
  window.crypto.getRandomValues(buf);
  return b64url(buf);
}

async function s256Challenge(verifier) {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

/**
 * @param {Object}   options
 * @param {Function} options.getConfig   () => ({ hostedUiDomain, userPoolClientId,
 *                                       ssoProviders: [{name,label,visible}],
 *                                       ssoProvider })  — read LIVE on every
 *                                       call, so late-loaded runtime config
 *                                       (config.json fetched after boot) works.
 * @param {string}   options.storageKey  sessionStorage key for the pending
 *                                       sign-in (verifier + state). Give each
 *                                       app its own, e.g. 'primordia.pkce'.
 * @param {string}  [options.legacyProviderLabel] label used when only the old
 *                                       single `ssoProvider` config shape exists.
 * @param {string}  [options.notConfiguredMessage] overrides the default copy.
 */
export function createSsoClient(options) {
  const { getConfig, storageKey } = options;
  const NOT_CONFIGURED_MSG = options.notConfiguredMessage || DEFAULT_NOT_CONFIGURED_MSG;

  function hostedUiBase() {
    const raw = String((getConfig().hostedUiDomain || '')).trim();
    if (!raw) return null;
    const host = raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    return host ? `https://${host}` : null;
  }

  // Must match a callback URL registered on the app client exactly — both apps
  // register the bare origin.
  const redirectUri = () => window.location.origin;

  function clearPending() {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {
      /* storage unavailable — nothing to clear */
    }
  }

  // Drop code/state/error from the address bar so a refresh cannot replay them
  // and so nothing sensitive survives in history or a copied URL.
  function stripParams() {
    try {
      const url = new URL(window.location.href);
      ['code', 'state', 'error', 'error_description', 'error_uri'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    } catch (e) {
      /* history API unavailable — non-fatal */
    }
  }

  function looksUnconfigured(code, description) {
    const d = (description || '').toLowerCase();
    return NOT_CONFIGURED_CODES.includes(code) || d.includes('identity provider') || d.includes('identity_provider');
  }

  // The configured provider list. `ssoProviders` (a list) is the current
  // shape; a single `ssoProvider` name is honoured as a legacy fallback so a
  // stage whose runtime config predates the list keeps working.
  function providers() {
    const cfg = getConfig();
    const list = Array.isArray(cfg.ssoProviders) ? cfg.ssoProviders : [];
    const out = list
      .filter((p) => p && typeof p.name === 'string' && p.name.trim())
      .map((p) => ({
        name: p.name.trim(),
        label: (p.label || '').trim() || `Sign in with ${p.name.trim()}`,
        // Absent means hidden: a provider that has not said it is safe to
        // show on a shared login page is not shown. See visibleProviders().
        visible: p.visible === true,
      }));
    if (out.length) return out;
    const legacy = String(cfg.ssoProvider || '').trim();
    return legacy
      ? [{ name: legacy, label: options.legacyProviderLabel || `Sign in with ${legacy}`, visible: true }]
      : [];
  }

  // The providers that get a button. In a multi-tenant app one login page
  // serves every client, so a button per federated client publishes the
  // vendor's customer list — hidden providers are reached by ?idp=<name>.
  const visibleProviders = () => providers().filter((p) => p.visible);

  const available = () => !!hostedUiBase() && !!getConfig().userPoolClientId && providers().length > 0;

  // The default provider for a bare begin(): the first VISIBLE one, so it can
  // never silently send a user to a hidden (client-specific) directory.
  function defaultProviderName() {
    const shown = visibleProviders();
    return shown.length ? shown[0].name : null;
  }

  // `?idp=<name>` — matched against the configured list rather than trusted:
  // the value comes out of the address bar, and forwarding an arbitrary string
  // to Cognito's identity_provider parameter is a redirect no app should offer.
  function requestedProvider() {
    try {
      const asked = (new URLSearchParams(window.location.search).get('idp') || '').trim();
      if (!asked) return null;
      const match = providers().find((p) => p.name.toLowerCase() === asked.toLowerCase());
      return match ? match.name : null;
    } catch (e) {
      return null;
    }
  }

  // True when the current URL is a hosted-UI callback, so a UI can show its
  // exchange spinner on first paint instead of flashing the login form.
  function hasRedirect() {
    const q = new URLSearchParams(window.location.search);
    return q.has('code') || q.has('error');
  }

  // Redirect to the hosted UI. Never resolves on success — the browser leaves.
  async function begin(provider) {
    const base = hostedUiBase();
    const cfg = getConfig();
    const idp = provider || defaultProviderName();
    if (!base || !cfg.userPoolClientId || !idp) throw new Error(NOT_CONFIGURED_MSG);
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('This browser cannot perform a secure sign-in (Web Crypto unavailable over an insecure connection).');
    }

    const verifier = randomToken(32);
    const state = randomToken(16);
    const challenge = await s256Challenge(verifier);
    const uri = redirectUri();
    sessionStorage.setItem(storageKey, JSON.stringify({ verifier, state, provider: idp, redirectUri: uri }));

    const params = new URLSearchParams({
      identity_provider: idp,
      client_id: cfg.userPoolClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: uri,
      // The CSRF state MUST ride to Cognito or the whole guard is theatre —
      // see the header. Cognito echoes it back on the callback.
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    window.location.assign(`${base}/oauth2/authorize?${params.toString()}`);
  }

  async function exchangeCode(base, cfg, code, stored) {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.userPoolClientId,
      code,
      redirect_uri: stored.redirectUri || redirectUri(),
      code_verifier: stored.verifier,
    });
    const res = await fetch(`${base}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    if (!res.ok || !data.id_token) {
      const err = data.error || `HTTP ${res.status}`;
      const desc = data.error_description ? `: ${data.error_description}` : '';
      return { failed: true, error: err, description: data.error_description, message: `Token exchange failed (${err}${desc}).` };
    }
    return { failed: false, data };
  }

  /**
   * Complete a redirect back from the hosted UI. Statuses:
   *   'none'            not a callback — show the ordinary login UI
   *   'not-configured'  the provider is not (correctly) attached yet
   *   'stale'           a callback with no sign-in pending (old URL reopened)
   *   'error'           a real failure — including a state mismatch
   *   'ok'              tokens in `.tokens` ({id_token, access_token, refresh_token})
   * Every exit path strips the one-time params and clears the verifier.
   */
  async function complete() {
    const q = new URLSearchParams(window.location.search);
    const code = q.get('code');
    const returnedState = q.get('state');
    const errParam = q.get('error');
    const errDesc = q.get('error_description');

    if (!code && !errParam) return { status: 'none' };

    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch (e) {
      stored = null;
    }

    // The IdP or Cognito rejected the request before any code was issued.
    if (errParam) {
      stripParams();
      clearPending();
      return looksUnconfigured(errParam, errDesc)
        ? { status: 'not-configured', message: NOT_CONFIGURED_MSG }
        : { status: 'error', message: `Sign-in failed (${errParam}${errDesc ? `: ${errDesc}` : ''}).` };
    }

    // No sign-in pending: stale, not hostile — see the header.
    if (!stored || !stored.state) {
      stripParams();
      clearPending();
      return {
        status: 'stale',
        message: 'That sign-in link had already been used or had expired, so it was ignored. '
          + 'Please sign in again — and avoid reopening old sign-in pages from the address bar’s suggestions or the Back button.',
      };
    }

    // CSRF guard. A pending sign-in exists and the response does not match it —
    // THIS is the genuinely suspicious case. Fail closed, never exchange the code.
    if (!returnedState || stored.state !== returnedState) {
      stripParams();
      clearPending();
      return {
        status: 'error',
        message: 'Sign-in could not be verified (state mismatch) and was stopped. Please start sign-in again from this page.',
      };
    }

    try {
      const base = hostedUiBase();
      const cfg = getConfig();
      if (!base || !cfg.userPoolClientId) return { status: 'not-configured', message: NOT_CONFIGURED_MSG };

      const out = await exchangeCode(base, cfg, code, stored);
      if (out.failed) {
        return looksUnconfigured(out.error, out.description)
          ? { status: 'not-configured', message: NOT_CONFIGURED_MSG }
          : { status: 'error', message: out.message };
      }
      return { status: 'ok', tokens: out.data };
    } catch (e) {
      return { status: 'error', message: (e && e.message) || 'Sign-in failed.' };
    } finally {
      // Every exit path: no replayable code in the URL, no verifier left behind.
      stripParams();
      clearPending();
    }
  }

  // Where to send the browser to tear down the HOSTED session too. Clearing
  // local storage alone leaves the hosted-UI cookie in place, so the next
  // sign-in bounces straight back through the IdP and the user appears to
  // have never signed out. Null when federation is not configured.
  function logoutUrl() {
    const base = hostedUiBase();
    const clientId = getConfig().userPoolClientId;
    if (!base || !clientId) return null;
    const params = new URLSearchParams({ client_id: clientId, logout_uri: redirectUri() });
    return `${base}/logout?${params.toString()}`;
  }

  return {
    available,
    providers,
    visibleProviders,
    defaultProviderName,
    requestedProvider,
    hasRedirect,
    begin,
    complete,
    logoutUrl,
  };
}

// A static page (Scout) loads this with <script type="module"> and reaches it
// from inline classic scripts via the window global.
if (typeof window !== 'undefined') window.createSsoClient = createSsoClient;
